import { useEffect, useRef, useState, useCallback } from "react";
import Hls from "hls.js";
import {
  Play, Pause, Volume2, VolumeX, Maximize, Minimize,
  RotateCcw, RotateCw, Loader2,
} from "lucide-react";

function fmt(t) {
  if (!t || isNaN(t)) return "0:00";
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export const VideoPlayer = ({ src, type = "mp4", fallback = "", poster, title }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const hideTimer = useRef(null);

  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffering, setBuffering] = useState(true);
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [error, setError] = useState(null);

  // Attach source (HLS or native)
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;
    setError(null);
    setBuffering(true);
    let hls;

    if (type === "hls") {
      if (video.canPlayType("application/vnd.apple.mpegurl")) {
        video.src = src; // Safari / iOS native HLS
      } else if (Hls.isSupported()) {
        hls = new Hls({ enableWorker: true });
        hls.loadSource(src);
        hls.attachMedia(video);
        hls.on(Hls.Events.ERROR, (_e, data) => {
          if (data.fatal) setError("Unable to load this stream.");
        });
      } else {
        setError("HLS is not supported in this browser.");
      }
    } else {
      // mp4: sources rendered as <source> children for multi-codec support
      video.load();
    }

    return () => {
      if (hls) hls.destroy();
    };
  }, [src, type, fallback]);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play().catch(() => setError("Tap play to start."));
    else v.pause();
  }, []);

  const seek = (val) => {
    const v = videoRef.current;
    if (v && duration) {
      v.currentTime = (val / 100) * duration;
      setCurrent(v.currentTime);
    }
  };

  const skip = (secs) => {
    const v = videoRef.current;
    if (v) v.currentTime = Math.min(Math.max(0, v.currentTime + secs), duration);
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  const changeVolume = (val) => {
    const v = videoRef.current;
    if (!v) return;
    v.volume = val;
    v.muted = val === 0;
    setVolume(val);
    setMuted(val === 0);
  };

  const toggleFullscreen = async () => {
    const el = containerRef.current;
    const v = videoRef.current;
    try {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) {
        if (el.requestFullscreen) await el.requestFullscreen();
        else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
        else if (v.webkitEnterFullscreen) v.webkitEnterFullscreen(); // iOS Safari
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    const onFsChange = () => setFullscreen(!!(document.fullscreenElement || document.webkitFullscreenElement));
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);
    return () => {
      document.removeEventListener("fullscreenchange", onFsChange);
      document.removeEventListener("webkitfullscreenchange", onFsChange);
    };
  }, []);

  const nudgeControls = useCallback(() => {
    setShowControls(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setShowControls(false);
    }, 3000);
  }, []);

  const progress = duration ? (current / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      data-testid="video-player"
      className="relative w-full aspect-video bg-black overflow-hidden group select-none touch-none"
      onMouseMove={nudgeControls}
      onClick={nudgeControls}
    >
      <video
        ref={videoRef}
        poster={poster}
        playsInline
        webkit-playsinline="true"
        className="w-full h-full object-contain bg-black"
        onClick={togglePlay}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onWaiting={() => setBuffering(true)}
        onPlaying={() => setBuffering(false)}
        onCanPlay={() => setBuffering(false)}
        onLoadedMetadata={(e) => setDuration(e.target.duration)}
        onTimeUpdate={(e) => setCurrent(e.target.currentTime)}
        onError={() => setError("This video failed to load.")}
      >
        {type === "mp4" && fallback && <source src={fallback} type="video/webm" />}
        {type === "mp4" && <source src={src} />}
      </video>

      {/* Buffering / error overlay */}
      {(buffering && !error) && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <Loader2 className="animate-spin text-[#00F0FF]" size={48} />
        </div>
      )}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/70 p-6 text-center" data-testid="player-error">
          <p className="text-white/80">{error}</p>
          <button onClick={togglePlay} className="rounded-full bg-[#00F0FF] text-black font-semibold px-6 py-2">Retry</button>
        </div>
      )}

      {/* Center tap play button */}
      {!playing && !buffering && !error && (
        <button
          onClick={togglePlay}
          data-testid="player-center-play"
          className="absolute inset-0 m-auto w-20 h-20 flex items-center justify-center rounded-full bg-[#00F0FF]/90 text-black glow-cyan hover:scale-105 transition-transform"
          aria-label="Play"
        >
          <Play size={40} fill="currentColor" />
        </button>
      )}

      {/* Controls chrome */}
      <div
        className={`absolute bottom-0 left-0 right-0 px-4 sm:px-6 pb-4 pt-16 bg-gradient-to-t from-black/90 via-black/40 to-transparent transition-opacity duration-300 ${showControls ? "opacity-100" : "opacity-0 pointer-events-none"}`}
      >
        {title && <p className="hidden sm:block font-heading text-white/90 mb-2 truncate">{title}</p>}

        {/* Scrub bar */}
        <div className="flex items-center gap-3 mb-2">
          <span className="text-xs text-white/70 font-mono-ui w-10 text-right shrink-0">{fmt(current)}</span>
          <div className="relative flex-1 h-2 flex items-center">
            <div className="absolute h-1 w-full rounded-full bg-white/20" />
            <div className="absolute h-1 rounded-full bg-[#00F0FF]" style={{ width: `${progress}%` }} />
            <input
              type="range"
              min="0" max="100" step="0.1"
              value={progress}
              onChange={(e) => seek(parseFloat(e.target.value))}
              data-testid="player-scrub"
              className="scrub absolute w-full h-4"
              aria-label="Seek"
            />
          </div>
          <span className="text-xs text-white/70 font-mono-ui w-10 shrink-0">{fmt(duration)}</span>
        </div>

        {/* Buttons row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1 sm:gap-2">
            <button onClick={togglePlay} data-testid="player-playpause" className="w-11 h-11 flex items-center justify-center text-white hover:text-[#00F0FF] transition-colors" aria-label="Play/Pause">
              {playing ? <Pause size={26} fill="currentColor" /> : <Play size={26} fill="currentColor" />}
            </button>
            <button onClick={() => skip(-10)} className="w-11 h-11 flex items-center justify-center text-white hover:text-[#00F0FF] transition-colors" aria-label="Back 10s">
              <RotateCcw size={22} />
            </button>
            <button onClick={() => skip(10)} className="w-11 h-11 flex items-center justify-center text-white hover:text-[#00F0FF] transition-colors" aria-label="Forward 10s">
              <RotateCw size={22} />
            </button>
            <div className="flex items-center gap-1">
              <button onClick={toggleMute} data-testid="player-mute" className="w-11 h-11 flex items-center justify-center text-white hover:text-[#00F0FF] transition-colors" aria-label="Mute">
                {muted || volume === 0 ? <VolumeX size={22} /> : <Volume2 size={22} />}
              </button>
              <input
                type="range" min="0" max="1" step="0.05"
                value={muted ? 0 : volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value))}
                className="scrub hidden sm:block w-20 h-1 rounded-full"
                style={{ background: `linear-gradient(to right,#00F0FF ${(muted ? 0 : volume) * 100}%,rgba(255,255,255,0.2) ${(muted ? 0 : volume) * 100}%)` }}
                aria-label="Volume"
              />
            </div>
          </div>

          <button onClick={toggleFullscreen} data-testid="player-fullscreen" className="w-11 h-11 flex items-center justify-center text-white hover:text-[#00F0FF] transition-colors" aria-label="Fullscreen">
            {fullscreen ? <Minimize size={22} /> : <Maximize size={22} />}
          </button>
        </div>
      </div>
    </div>
  );
};
